import React, { useState, useCallback } from 'react';

/**
 * Componente per renderizzare un gruppo di checkbox in orizzontale
 * @description Renderizza un gruppo di checkbox in orizzontale con colori coordinati agli stati della DataTable
 * @param {Array} options - Array di opzioni con value, label e proprietà colore
 * @param {Array} selectedValues - Array di valori selezionati
 * @param {Function} onChange - Callback per il cambio selezione (singolo click)
 * @param {Function} onDoubleClick - Callback per il doppio click esclusivo
 * @param {string} [themeColor='emerald'] - Il colore del tema per gli elementi selezionati (deprecato, usa proprietà colore nelle options)
 */
const HorizontalCheckboxGroup = ({
  options = [],
  selectedValues = [],
  onChange,
  onDoubleClick,
  showSelectAll = true,
  labelKey = 'label',
  valueKey = 'value',
  colorKey = 'color',
  selectedColorKey = 'selectedColor',
  borderColorKey = 'borderColor',
  textColorKey = 'textColor',
  themeColor = 'emerald',
}) => {

  // Gestisce il click su una checkbox
  const handleCheckboxChange = (value) => {
    let newSelectedValues;
    
    if (selectedValues.includes(value)) {
      // Rimuovi dalla selezione
      newSelectedValues = selectedValues.filter(v => v !== value);
    } else {
      // Aggiungi alla selezione
      newSelectedValues = [...selectedValues, value];
    }
    
    onChange(newSelectedValues);
  };

  // Gestisce il doppio click esclusivo
  const handleDoubleClick = useCallback((value) => {
    if (onDoubleClick) {
      onDoubleClick(value);
    }
  }, [onDoubleClick]);

  // Funzione per ottenere i colori degli stati basati sui mapping IDENTICI a DataTable
  const getStatusColors = (option, isSelected) => {
    // Se l'option ha proprietà colore specifiche, usale (AGGIORNATO per maggiore chiarezza)
    if (option[colorKey] && option[selectedColorKey]) {
      return {
        bg: isSelected ? option[selectedColorKey] : option[colorKey],
        text: isSelected ? 'text-white font-medium' : (option[textColorKey] || 'text-gray-800 dark:text-gray-200'),
        border: isSelected ? option[selectedColorKey].replace('bg-', 'border-') : (option[borderColorKey] || 'border-gray-300'),
        hover: isSelected ? option[selectedColorKey] : 'hover:bg-gray-100 dark:hover:bg-gray-700'
      };
    }

    // Fallback ai colori IDENTICI al DataTable
    const statusCode = option[valueKey];
    const baseColors = {
      'I': { // Inserito - Verde (IDENTICO a DataTable)
        bg: isSelected ? 'bg-green-600' : 'bg-green-50 dark:bg-green-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-green-800 dark:text-green-200',
        border: isSelected ? 'border-green-600' : 'border-green-300',
        hover: isSelected ? 'bg-green-600' : 'hover:bg-green-100 dark:hover:bg-green-800/50'
      },
      'C': { // Cri - Rosa (IDENTICO a DataTable)
        bg: isSelected ? 'bg-pink-600' : 'bg-pink-50 dark:bg-pink-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-pink-800 dark:text-pink-200',
        border: isSelected ? 'border-pink-600' : 'border-pink-300',
        hover: isSelected ? 'bg-pink-600' : 'hover:bg-pink-100 dark:hover:bg-pink-800/50'
      },
      'F': { // Filippo - Giallo (IDENTICO a DataTable)
        bg: isSelected ? 'bg-yellow-600' : 'bg-yellow-50 dark:bg-yellow-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-yellow-800 dark:text-yellow-200',
        border: isSelected ? 'border-yellow-600' : 'border-yellow-300',
        hover: isSelected ? 'bg-yellow-600' : 'hover:bg-yellow-100 dark:hover:bg-yellow-800/50'
      },
      'M': { // Marco - Blu (IDENTICO a DataTable)
        bg: isSelected ? 'bg-blue-600' : 'bg-blue-50 dark:bg-blue-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-blue-800 dark:text-blue-200',
        border: isSelected ? 'border-blue-600' : 'border-blue-300',
        hover: isSelected ? 'bg-blue-600' : 'hover:bg-blue-100 dark:hover:bg-blue-800/50'
      },
      'P': { // Paul - Amber (IDENTICO a DataTable)
        bg: isSelected ? 'bg-amber-600' : 'bg-amber-100 dark:bg-amber-400/40',
        text: isSelected ? 'text-white font-medium' : 'text-amber-800 dark:text-amber-200',
        border: isSelected ? 'border-amber-600' : 'border-amber-300',
        hover: isSelected ? 'bg-amber-600' : 'hover:bg-amber-200 dark:hover:bg-amber-700/50'
      },
      'T': { // Terminato - Bianco/Grigio (IDENTICO a DataTable)
        bg: isSelected ? 'bg-gray-600' : 'bg-white dark:bg-gray-800',
        text: isSelected ? 'text-white font-medium' : 'text-gray-800 dark:text-gray-200',
        border: isSelected ? 'border-gray-600' : 'border-gray-300',
        hover: isSelected ? 'bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
      },
      'D': { // Da rilasciare - Ciano (IDENTICO a DataTable)
        bg: isSelected ? 'bg-cyan-600' : 'bg-cyan-50 dark:bg-cyan-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-cyan-800 dark:text-cyan-200',
        border: isSelected ? 'border-cyan-600' : 'border-cyan-300',
        hover: isSelected ? 'bg-cyan-600' : 'hover:bg-cyan-100 dark:hover:bg-cyan-800/50'
      },
      'S': { // Sospesa - Arancione (IDENTICO a DataTable)
        bg: isSelected ? 'bg-orange-600' : 'bg-orange-50 dark:bg-orange-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-orange-800 dark:text-orange-200',
        border: isSelected ? 'border-orange-600' : 'border-orange-300',
        hover: isSelected ? 'bg-orange-600' : 'hover:bg-orange-100 dark:hover:bg-orange-800/50'
      },
      'U': { // Urgente - Rosso (IDENTICO a DataTable)
        bg: isSelected ? 'bg-red-600' : 'bg-red-50 dark:bg-red-900/30',
        text: isSelected ? 'text-white font-medium' : 'text-red-800 dark:text-red-200',
        border: isSelected ? 'border-red-600' : 'border-red-300',
        hover: isSelected ? 'bg-red-600' : 'hover:bg-red-100 dark:hover:bg-red-800/50'
      }
    };

    return baseColors[statusCode] || {
      bg: isSelected ? `bg-${themeColor}-600` : `bg-${themeColor}-50 dark:bg-${themeColor}-900/30`,
      text: isSelected ? 'text-white font-medium' : `text-${themeColor}-800 dark:text-${themeColor}-200`,
      border: isSelected ? `border-${themeColor}-600` : `border-${themeColor}-300`,
      hover: isSelected ? `bg-${themeColor}-600` : `hover:bg-${themeColor}-100 dark:hover:bg-${themeColor}-800/50`
    };
  };

  const selectAll = () => {
    onChange(options.map(option => option[valueKey]));
  };

  const deselectAll = () => {
    onChange([]);
  };

  return (
    <div className="space-y-3">
      {showSelectAll && (
        <div className="flex gap-2">
          <button
            onClick={selectAll}
            className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-md hover:bg-blue-200 dark:hover:bg-blue-800/50 transition-colors"
          >
            Seleziona tutti
          </button>
          <button
            onClick={deselectAll}
            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Deseleziona tutti
          </button>
        </div>
      )}
      
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const value = option[valueKey];
          const label = option[labelKey];
          const isSelected = selectedValues.includes(value);
          const colors = getStatusColors(option, isSelected);

          return (
            <label
              key={value}
              className={`
                inline-flex items-center px-4 py-2 rounded-lg cursor-pointer transition-all duration-200 border-2
                ${colors.bg} ${colors.text} ${colors.border} ${colors.hover}
                ${isSelected ? 'ring-2 ring-offset-1 ring-blue-500 dark:ring-offset-gray-800' : ''}
                transform hover:scale-105 active:scale-95
              `}
              onDoubleClick={(e) => {
                e.preventDefault();
                handleDoubleClick(value);
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleCheckboxChange(value)}
                className="sr-only" // Nasconde la checkbox visivamente ma mantiene l'accessibilità
              />
              <span className="font-medium text-sm">
                {label}
              </span>
              {isSelected && (
                <span className="ml-2 text-xs opacity-75">✓</span>
              )}
            </label>
          );
        })}
      </div>
      
      {selectedValues.length > 0 && (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {selectedValues.length} elementi selezionati
          {selectedValues.length === 1 && onDoubleClick && (
            <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
              (Doppio click per selezione esclusiva)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default HorizontalCheckboxGroup;